import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CompanyInfo } from '../../types/kpi.types';

interface CompanyComboboxProps {
  companies: CompanyInfo[];
  value: string; // companyId or 'all'
  onChange: (companyId: string) => void;
}

export const CompanyCombobox: React.FC<CompanyComboboxProps> = ({ companies, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [highlighted, setHighlighted] = useState(0);

  const selectedName =
    value === 'all'
      ? 'All Companies'
      : companies.find((c) => c.id.toString() === value)?.name ?? 'All Companies';

  const filtered = [
    { id: 'all', name: 'All Companies', numberOfEmployees: null },
    ...companies,
  ].filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      setHighlighted(0);
    }
  }, [open]);

  const select = useCallback(
    (id: string) => {
      onChange(id);
      setOpen(false);
      setSearch('');
    },
    [onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && filtered[highlighted]) {
      select(filtered[highlighted].id.toString());
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', minWidth: 260 }}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          border: open ? '1.5px solid #4CAF50' : '1.5px solid #ddd',
          borderRadius: 8,
          backgroundColor: '#fff',
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 500,
          color: '#333',
          width: '100%',
          boxShadow: open ? '0 0 0 3px rgba(76,175,80,0.12)' : '0 1px 3px rgba(0,0,0,0.08)',
          transition: 'all 0.15s',
          minWidth: 260,
        }}
      >
        <span style={{ fontSize: 16 }}>{value === 'all' ? '📊' : '🏢'}</span>
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedName}
        </span>
        <span style={{ color: '#999', fontSize: 12, flexShrink: 0 }}>
          {open ? '▲' : '▼'}
        </span>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 1000,
            backgroundColor: '#fff',
            border: '1.5px solid #e0e0e0',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            width: 320,
            overflow: 'hidden',
          }}
        >
          {/* Search input */}
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#999', fontSize: 14 }}>🔍</span>
              <input
                ref={inputRef}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setHighlighted(0); }}
                onKeyDown={handleKeyDown}
                placeholder='Search company...'
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  fontSize: 14,
                  color: '#333',
                  backgroundColor: 'transparent',
                }}
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#aaa', fontSize: 16, padding: 0, lineHeight: 1 }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Results list */}
          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '16px 14px', color: '#999', fontSize: 14, textAlign: 'center' }}>
                No companies found
              </div>
            ) : (
              filtered.map((c, i) => {
                const isSelected = c.id.toString() === value;
                const isHighlighted = i === highlighted;
                return (
                  <div
                    key={c.id}
                    onMouseEnter={() => setHighlighted(i)}
                    onClick={() => select(c.id.toString())}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '9px 14px',
                      cursor: 'pointer',
                      backgroundColor: isSelected
                        ? '#f0faf0'
                        : isHighlighted
                        ? '#f7f7f7'
                        : 'transparent',
                      borderLeft: isSelected ? '3px solid #4CAF50' : '3px solid transparent',
                      transition: 'background 0.1s',
                    }}
                  >
                    <span style={{ fontSize: 15 }}>{c.id === 'all' ? '📊' : '🏢'}</span>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <div style={{
                        fontSize: 14,
                        fontWeight: isSelected ? 600 : 400,
                        color: '#222',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {c.name}
                      </div>
                      {c.numberOfEmployees !== null && (
                        <div style={{ fontSize: 11, color: '#999', marginTop: 1 }}>
                          {c.numberOfEmployees} employees
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <span style={{ color: '#4CAF50', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>✓</span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer hint */}
          <div style={{
            padding: '7px 14px',
            borderTop: '1px solid #f0f0f0',
            fontSize: 11,
            color: '#bbb',
            display: 'flex',
            gap: 12,
          }}>
            <span>↑↓ navigate</span>
            <span>↵ select</span>
            <span>esc close</span>
          </div>
        </div>
      )}
    </div>
  );
};
